import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Select, Typography, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';
import ProposalEditorModal, { PROPOSAL_STATUS_COLORS, proposalColumns } from '../../components/ProposalEditorModal';

/** Firm-wide proposal list (xlsx "Proposal List") - against a Lead pre-conversion or a Client. */
export default function ProposalsPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [leads, setLeads] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [proposals, setProposals] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = () => {
    if (!companyId) return;
    setLoading(true);
    Promise.all([
      request.get('/proposals', { company_id: companyId, ...(statusFilter ? { status: statusFilter } : {}) }).then((r) => r.json()),
      request.get('/leads', { company_id: companyId }).then((r) => r.json()),
      request.get('/clients', { company_id: companyId }).then((r) => r.json()),
    ])
      .then(([proposalsRes, leadsRes, clientsRes]) => {
        if (proposalsRes.status) setProposals(proposalsRes.data);
        if (leadsRes.status) setLeads(leadsRes.data.filter((l) => l.status !== 'CONVERTED' && l.status !== 'LOST'));
        if (clientsRes.status) setClients(clientsRes.data);
      })
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchAll, [companyId, statusFilter]);

  const columns = proposalColumns({
    withFor: {
      title: 'For',
      key: 'for',
      render: (_, p) =>
        p.Client ? <Link to={`/clients/${p.Client.id}`}>{p.Client.client_name}</Link> : p.Lead ? <Link to={`/leads/${p.Lead.id}`}>{p.Lead.lead_name} (lead)</Link> : '-',
    },
    onEdit: (p) => {
      setEditing(p);
      setEditorOpen(true);
    },
    onChange: fetchAll,
  });

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Proposals
        </Typography.Title>
        <Space>
          {isSystemAdmin && (
            <Select style={{ width: 200 }} value={companyId} placeholder="Company" onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          )}
          <Select
            allowClear
            style={{ width: 150 }}
            placeholder="All statuses"
            value={statusFilter}
            onChange={setStatusFilter}
            options={Object.keys(PROPOSAL_STATUS_COLORS).map((s) => ({ value: s, label: s }))}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
          >
            New Proposal
          </Button>
        </Space>
      </Space>

      <Table rowKey="id" columns={columns} dataSource={proposals} loading={loading} expandable={{ expandedRowRender: (p) => <div style={{ whiteSpace: 'pre-wrap' }}>{p.details || 'No proposal details recorded.'}</div> }} />

      <ProposalEditorModal open={editorOpen} onClose={() => setEditorOpen(false)} onSaved={fetchAll} targets={{ leads, clients }} proposal={editing} />
    </div>
  );
}
